import { useEffect, useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import MuiButton from "@mui/material/Button";

import { buttonSx } from "./buttonStyles";

// Spinner time if the page doesn't change (e.g. the link points to the current page)
const LOADING_RESET_MS = 1500;

interface ButtonLinkProps {
    to: string; // Internal route
    className?: string;
    children: ReactNode;
}

// A button that looks like the site's Button but is a real link:
// right-click, middle-click and "open in new tab" all work.
const ButtonLink = ({ to, className, children }: ButtonLinkProps) => {
    const [loading, setLoading] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    // Don't update state after the components is gone
    useEffect(() => () => clearTimeout(timerRef.current), []);

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (loading) {
            event.preventDefault();
            return;
        }

        // Ctrl/Cmd/Shift-click opens a new tab, so this page stays and needs no spinner
        if (event.ctrlKey || event.metaKey || event.shiftKey) return;

        setLoading(true);
        timerRef.current = setTimeout(() => setLoading(false), LOADING_RESET_MS);
    };

    return (
        <MuiButton
            component={RouterLink}
            to={to}
            onClick={handleClick}
            loading={loading}
            variant="contained"
            className={className}
            sx={buttonSx}
        >
            {children}
        </MuiButton>
    );
};

export default ButtonLink;